import React, { useEffect, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  TextField,
  Typography,
  Alert,
  Stack,
} from "@mui/material";
import useAuth from "../../hooks/useAuth";
import {
  updateMyOrganization,
  completeTenantWelcome,
} from "../../api/onboarding";
import * as authService from "../../api/auth";
import { storeAuthToken } from "../../utils/storage";
import { useNotify } from "../../utils/useNotify";
import { APP_HOME_PATH } from "../../config/routes";

export default function FarmSetup() {
  const navigate = useNavigate();
  const { user, refreshProfile, isLoading } = useAuth();
  const notify = useNotify();
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [saveLoading, setSaveLoading] = useState(false);
  const [dashLoading, setDashLoading] = useState(false);

  const tenantName = user?.profile?.tenant_name ?? "";

  useEffect(() => {
    setName(tenantName || "");
  }, [tenantName]);

  if (isLoading) {
    return null;
  }
  if (!user || user.tenant_id == null) {
    return <Navigate to={APP_HOME_PATH} replace />;
  }

  const onSaveName = async (e) => {
    e.preventDefault();
    setError("");
    const trimmed = name.trim();
    if (!trimmed) {
      setError("Enter a farm name.");
      return;
    }
    setSaveLoading(true);
    try {
      await updateMyOrganization(trimmed);
      const { token } = await authService.refreshJwt();
      storeAuthToken(token);
      await refreshProfile();
      notify.success("Farm name saved.");
    } catch (err) {
      setError(
        err?.response?.data?.detail ||
          err?.message ||
          "Could not update organization name."
      );
    } finally {
      setSaveLoading(false);
    }
  };

  const onGoToDashboard = async () => {
    setError("");
    setDashLoading(true);
    try {
      await completeTenantWelcome();
      const { token } = await authService.refreshJwt();
      storeAuthToken(token);
      await refreshProfile();
      notify.info("Welcome! Taking you to your dashboard.");
      navigate(APP_HOME_PATH, { replace: true });
    } catch (err) {
      setError(
        err?.response?.data?.detail ||
          err?.message ||
          "Could not continue. Try again."
      );
    } finally {
      setDashLoading(false);
    }
  };

  return (
    <Box sx={{ display: "flex", justifyContent: "center", pt: 4, px: 2 }}>
      <Card sx={{ maxWidth: 520, width: "100%" }}>
        <CardContent>
          <Typography variant="h5" gutterBottom>
            Your farm is ready
          </Typography>
          <Typography color="text.secondary" paragraph>
            We created starter roles (Staff, Manager, Bookkeeper) so you can
            invite people without giving everyone full admin access. Confirm
            your farm name, then invite your team.
          </Typography>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {typeof error === "string" ? error : JSON.stringify(error)}
            </Alert>
          )}
          <Box component="form" onSubmit={onSaveName} sx={{ mb: 3 }}>
            <TextField
              label="Farm / organization name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              fullWidth
              margin="normal"
              helperText="Shown to your team"
            />
            <Button
              type="submit"
              variant="outlined"
              disabled={saveLoading || !name.trim()}
              sx={{ mt: 1 }}
            >
              {saveLoading ? "Saving…" : "Save name"}
            </Button>
          </Box>
          <Stack spacing={1.5}>
            <Button
              component={Link}
              to="/admin/invites"
              variant="contained"
              fullWidth
            >
              Invite someone
            </Button>
            <Button
              variant="text"
              fullWidth
              disabled={dashLoading}
              onClick={onGoToDashboard}
            >
              {dashLoading ? "Loading…" : "Go to dashboard"}
            </Button>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}
